import { Participant } from '../types';

export class VideoGrid {
  private participants: Map<string, Participant> = new Map();
  private localParticipant: Participant | null = null;
  private activeSpeakerId: string | null = null;
  private pinnedParticipantId: string | null = null;
  private containerEl: HTMLElement | null = null;
  public onLowerHand?: (participantId: string) => void;

  public setLocalParticipant(p: Participant) {
    this.localParticipant = p;
    this.updateGrid();
  }

  public setParticipants(list: Participant[]) {
    const oldParticipants = new Map(this.participants);
    this.participants.clear();
    for (const p of list) {
      if (this.localParticipant && p.id === this.localParticipant.id) {
        continue;
      }
      const existing = oldParticipants.get(p.id);
      this.participants.set(p.id, {
        ...p,
        stream: p.stream || existing?.stream,
        screen_stream: p.screen_stream || existing?.screen_stream,
      });
    }
    this.updateGrid();
  }

  public addOrUpdateParticipant(p: Participant) {
    if (this.localParticipant && p.id === this.localParticipant.id) {
      const merged = { ...this.localParticipant, ...p };
      if (!this.hasSignificantChange(this.localParticipant, merged)) {
        this.localParticipant = merged;
        return;
      }
      this.localParticipant = merged;
    } else {
      const existing = this.participants.get(p.id);
      const merged = { ...existing, ...p };
      if (existing && !this.hasSignificantChange(existing, merged)) {
        this.participants.set(p.id, merged);
        return;
      }
      this.participants.set(p.id, merged);
    }
    this.updateGrid();
  }

  // Speaking fluctuations arrive several times a second per participant; they
  // are rendered exclusively by setActiveSpeaker's class-only fast path. Any
  // other change (mute, stream, role, ...) re-lays-out with node reuse.
  private hasSignificantChange(before: Participant, after: Participant): boolean {
    const strip = (o: Participant) => {
      const { is_speaking, stream, screen_stream, ...rest } = o as any;
      void is_speaking; void stream; void screen_stream;
      return JSON.stringify(rest);
    };
    return strip(before) !== strip(after);
  }

  public removeParticipant(pId: string) {
    this.participants.delete(pId);
    if (this.pinnedParticipantId === pId || this.pinnedParticipantId === `${pId}-screen`) {
      this.pinnedParticipantId = null;
    }
    this.updateGrid();
  }

  public setPinnedParticipant(pId: string | null) {
    this.pinnedParticipantId = pId;
    this.updateGrid();
  }

  public setActiveSpeaker(pId: string | null) {
    this.activeSpeakerId = pId;

    // Class-only fast path: speaking border + voice-wave indicators. Never
    // re-lays-out — tiles stay exactly where they are (Google Meet behavior:
    // speaking is a highlight, not a layout change).
    if (!this.containerEl) return;
    const allTiles = this.containerEl.querySelectorAll('.video-tile');
    allTiles.forEach(tile => {
      const id = tile.getAttribute('data-participant-id');
      const badge = tile.querySelector('.tile-speaking-badge') as HTMLElement | null;
      const wave = tile.querySelector('.voice-wave') as HTMLElement | null;
      if (id === pId) {
        tile.classList.add('speaking');
        if (badge) badge.style.display = 'inline-flex';
        if (wave) wave.classList.add('active');
      } else {
        tile.classList.remove('speaking');
        if (badge) badge.style.display = 'none';
        if (wave) wave.classList.remove('active');
      }
    });
  }

  public attachRemoteStream(participantId: string, stream: MediaStream) {
    // If stream ID or participant ID specifies screen
    if (participantId.endsWith('-screen')) {
      const baseId = participantId.replace('-screen', '');
      this.attachRemoteScreenStream(baseId, stream);
      return;
    }

    const p = this.participants.get(participantId);
    if (p) {
      p.stream = stream;
      p.video_muted = false;
      this.updateGrid();
      return;
    }

    // Fallback if ID doesn't match directly
    for (const part of this.participants.values()) {
      if (!part.stream) {
        part.stream = stream;
        part.video_muted = false;
        this.updateGrid();
        return;
      }
    }

    // Auto-create participant entry if participant isn't in roster yet
    this.participants.set(participantId, {
      id: participantId,
      display_name: 'Participant',
      role: 'participant',
      stream: stream,
      audio_muted: false,
      video_muted: false,
      screen_sharing: false,
      hand_raised: false,
    });
    this.updateGrid();
  }

  public attachRemoteScreenStream(participantId: string, stream: MediaStream) {
    const p = this.participants.get(participantId);
    if (p) {
      p.screen_stream = stream;
      p.screen_sharing = true;
      this.updateGrid();
      return;
    }

    for (const part of this.participants.values()) {
      if (part.screen_sharing) {
        part.screen_stream = stream;
        this.updateGrid();
        return;
      }
    }
  }

  public pinParticipant(id: string | null) {
    this.pinnedParticipantId = id;
    this.updateGrid();
  }

  public render(container: HTMLElement) {
    this.containerEl = container;
    this.updateGrid();
  }

  private updateGrid() {
    if (!this.containerEl) return;

    const sourceParticipants: Participant[] = [];
    if (this.localParticipant) {
      sourceParticipants.push(this.localParticipant);
    }
    for (const p of this.participants.values()) {
      sourceParticipants.push(p);
    }

    // Expand participants: every participant has a camera/user tile,
    // and if they are sharing their screen, they ALSO have a dedicated screen presentation tile!
    const allTiles: Participant[] = [];
    for (const p of sourceParticipants) {
      allTiles.push({
        ...p,
        is_screen_tile: false,
      });

      if (p.screen_sharing) {
        allTiles.push({
          id: `${p.id}-screen`,
          display_name: `${p.display_name} (Screen Share)`,
          role: p.role,
          avatar_url: p.avatar_url,
          audio_muted: true,
          video_muted: false,
          screen_sharing: true,
          is_screen_tile: true,
          is_speaking: false,
          stream: p.screen_stream,
          network_unstable: p.network_unstable,
          hand_raised: false,
        });
      }
    }

    const count = allTiles.length;

    // Spotlight (main stage) decision — Google Meet semantics:
    //   Priority 1: the user explicitly pinned a tile.
    //   Priority 2: someone is presenting — the presentation OWNS the stage.
    //     Active speakers NEVER displace it; they are only highlighted in the
    //     thumbnail strip.
    // There is deliberately no auto-spotlight of the active speaker: tiles
    // must not move when people talk.
    let spotlightTile: Participant | undefined = undefined;
    if (this.pinnedParticipantId) {
      spotlightTile = allTiles.find(t => t.id === this.pinnedParticipantId);
    }
    if (!spotlightTile) {
      spotlightTile = allTiles.find(t => t.is_screen_tile);
    }

    if (spotlightTile && count > 1) {
      const others = allTiles.filter(p => p.id !== spotlightTile!.id);
      this.renderLayout('spotlight-container', spotlightTile, others);
      return;
    }

    let gridClass = 'grid-cols-1';
    if (count === 2) gridClass = 'grid-cols-2';
    else if (count <= 4) gridClass = 'grid-cols-2';
    else if (count <= 9) gridClass = 'grid-cols-3';
    else gridClass = 'grid-cols-4';

    this.renderLayout(`video-grid-container ${gridClass}`, null, allTiles);
  }

  // renderLayout rebuilds the layout while REUSING existing tile DOM nodes
  // (matched by data-participant-id). Moved nodes keep their <video> decoding
  // alive, so layouts change without a single frame of video flicker.
  private renderLayout(rootClass: string, mainTile: Participant | null, stripTiles: Participant[]) {
    if (!this.containerEl) return;

    const oldNodes = new Map<string, HTMLElement>();
    this.containerEl.querySelectorAll('.video-tile').forEach(el => {
      const id = el.getAttribute('data-participant-id');
      if (id) oldNodes.set(id, el as HTMLElement);
    });

    const root = document.createElement('div');
    root.className = rootClass;
    if (rootClass === 'spotlight-container') {
      root.style.padding = '1rem';
      root.style.width = '100%';
      root.style.height = '100%';
      const main = document.createElement('div');
      main.className = 'spotlight-main';
      const strip = document.createElement('div');
      strip.className = 'spotlight-strip';
      root.appendChild(main);
      root.appendChild(strip);
      if (mainTile) main.appendChild(this.acquireTile(mainTile, oldNodes));
      for (const p of stripTiles) strip.appendChild(this.acquireTile(p, oldNodes));
    } else {
      for (const p of stripTiles) root.appendChild(this.acquireTile(p, oldNodes));
    }

    this.containerEl.innerHTML = '';
    this.containerEl.appendChild(root);
    this.bindTileEvents();
  }

  private acquireTile(p: Participant, oldNodes: Map<string, HTMLElement>): HTMLElement {
    const existing = oldNodes.get(p.id);
    if (existing) {
      oldNodes.delete(p.id);
      return this.refreshTile(existing, p);
    }
    const fresh = document.createElement('div');
    fresh.innerHTML = this.renderTileHTML(p);
    const node = fresh.firstElementChild as HTMLElement;
    this.applyMedia(node, p);
    return node;
  }

  // Surgically refresh a reused tile: speaking highlight, video visibility and
  // stream, mic icon, and name — without touching the <video> element itself.
  // If any structural badge changed (hand raised, recording, unstable, pin),
  // the tile HTML is regenerated in place. Returns the node now backing the tile.
  private refreshTile(node: HTMLElement, p: Participant): HTMLElement {
    const sig = `${!!p.hand_raised}|${!!p.network_unstable}|${!!p.is_recording}|${this.pinnedParticipantId === p.id}|${p.role}|${p.is_cohost ? 1 : 0}`;
    if (node.getAttribute('data-tile-sig') !== sig) {
      const fresh = document.createElement('div');
      fresh.innerHTML = this.renderTileHTML(p);
      const replacement = fresh.firstElementChild as HTMLElement;
      node.replaceWith(replacement);
      this.applyMedia(replacement, p);
      return replacement;
    }

    const isScreenTile = !!p.is_screen_tile;
    const isSpeaking = !isScreenTile && (this.activeSpeakerId === p.id || !!p.is_speaking);
    node.classList.toggle('speaking', isSpeaking);

    const hasLiveVideo = !!(p.stream && p.stream.getVideoTracks().length > 0 && p.stream.getVideoTracks().some(t => t.readyState !== 'ended'));
    const isVideoHidden = isScreenTile ? !hasLiveVideo : (p.video_muted || !hasLiveVideo);

    const video = node.querySelector('video') as HTMLVideoElement | null;
    if (video) {
      video.style.display = isVideoHidden ? 'none' : 'block';
      if (!isVideoHidden && p.stream && video.srcObject !== p.stream) {
        video.srcObject = p.stream;
        video.play().catch(() => {});
      }
    }

    const fallback = node.querySelector('.tile-avatar-fallback') as HTMLElement | null;
    if (fallback) fallback.style.display = isVideoHidden ? 'flex' : 'none';

    const micIcon = node.querySelector('.tile-mic-icon') as HTMLElement | null;
    if (micIcon && !isScreenTile) {
      micIcon.innerHTML = p.audio_muted ? MIC_MUTED_SVG : MIC_ON_SVG;
    }

    const nameEl = node.querySelector('.tile-name');
    const isLocal = this.localParticipant && (p.id === this.localParticipant.id || p.id === `${this.localParticipant.id}-screen`);
    if (nameEl) nameEl.textContent = `${p.display_name} ${isLocal && !isScreenTile ? '(You)' : ''}`;
    return node;
  }

  // Wire a freshly-created tile's video element to its MediaStream.
  private applyMedia(node: HTMLElement, p: Participant) {
    const videoEl = node.querySelector('video') as HTMLVideoElement | null;
    if (videoEl && p.stream) {
      videoEl.muted = true;
      if (videoEl.srcObject !== p.stream) {
        videoEl.srcObject = p.stream;
      }
      videoEl.play().catch(() => {});
    }
  }

  private renderTileHTML(p: Participant): string {
    const isLocal = this.localParticipant && (p.id === this.localParticipant.id || p.id === `${this.localParticipant.id}-screen`);
    const isScreenTile = !!p.is_screen_tile;
    const isSpeaking = !isScreenTile && (this.activeSpeakerId === p.id || !!p.is_speaking);
    const initials = isScreenTile ? '🖥️' : (p.display_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'U');
    const hasLiveVideo = !!(p.stream && p.stream.getVideoTracks().length > 0 && p.stream.getVideoTracks().some(t => t.readyState !== 'ended'));
    // For screen tile, video is NEVER hidden by camera mute (video_muted)!
    const isVideoHidden = isScreenTile ? !hasLiveVideo : (p.video_muted || !hasLiveVideo);
    const isPinned = this.pinnedParticipantId === p.id;
    const sig = `${!!p.hand_raised}|${!!p.network_unstable}|${!!p.is_recording}|${isPinned}|${p.role}|${p.is_cohost ? 1 : 0}`;

    return `
      <div class="video-tile ${isSpeaking ? 'speaking' : ''} ${isLocal && !isScreenTile ? 'mirrored' : ''} ${isScreenTile ? 'screen-tile' : ''}" data-participant-id="${p.id}" data-tile-sig="${sig}" ${isScreenTile ? 'data-screen="true"' : ''}>
        <video id="video-${p.id}" autoplay playsinline muted style="display: ${isVideoHidden ? 'none' : 'block'};"></video>

        <div class="tile-avatar-fallback" style="display: ${isVideoHidden ? 'flex' : 'none'};">
          <div class="avatar-circle">
            ${isScreenTile ? `🖥️` : (p.avatar_url ? `<img src="${p.avatar_url}" alt="${p.display_name}" class="tile-avatar-img" />` : initials)}
          </div>
          ${isScreenTile ? `<span style="font-size: 0.8rem; font-weight: 600; color: #94a3b8; margin-top: 6px;">Screen Sharing</span>` : ''}
        </div>

        <!-- Pin / Unpin Button -->
        <button type="button" class="btn-pin-tile ${isPinned ? 'pinned' : ''}" data-tile-id="${p.id}" title="${isPinned ? 'Unpin from main stage' : 'Pin to main stage'}">
          <span>📌</span>
          <span class="pin-text">${isPinned ? 'Unpin' : 'Pin'}</span>
        </button>

        ${p.hand_raised ? `
          <div class="tile-hand-raised" data-participant-id="${p.id}" title="Click to lower hand">
            ✋ Hand Raised
          </div>
        ` : ''}

        ${p.network_unstable ? `
          <div class="tile-network-badge" style="position: absolute; top: 10px; right: 88px; background: rgba(245, 158, 11, 0.95); color: #000; font-size: 0.72rem; font-weight: 700; padding: 3px 8px; border-radius: 9999px; display: inline-flex; align-items: center; gap: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.4); z-index: 5;" title="Unstable network connection detected">
            <span>📶</span>
            <span>Unstable</span>
          </div>
        ` : ''}

        ${isScreenTile ? `
          <div class="tile-screenshare-badge" style="position: absolute; top: 10px; left: 10px; background: rgba(59, 130, 246, 0.95); color: white; font-size: 0.72rem; font-weight: 700; padding: 3px 8px; border-radius: 9999px; display: inline-flex; align-items: center; gap: 4px; z-index: 5;">
            <span>🖥️</span>
            <span>Screen Presentation</span>
          </div>
        ` : (p.screen_sharing ? `
          <div class="tile-screenshare-badge" style="position: absolute; top: 10px; left: 10px; background: rgba(59, 130, 246, 0.95); color: white; font-size: 0.72rem; font-weight: 700; padding: 3px 8px; border-radius: 9999px; display: inline-flex; align-items: center; gap: 4px; z-index: 5;">
            <span>🖥️</span>
            <span>Sharing</span>
          </div>
        ` : '')}

        ${p.is_recording ? `
          <div class="tile-recording-badge" style="position: absolute; top: 10px; left: ${p.screen_sharing || isScreenTile ? '150px' : '10px'}; background: rgba(239, 68, 68, 0.95); color: white; font-size: 0.72rem; font-weight: 700; padding: 3px 8px; border-radius: 9999px; display: inline-flex; align-items: center; gap: 4px; z-index: 5;">
            <span style="width: 6px; height: 6px; border-radius: 50%; background: white;"></span>
            <span>REC</span>
          </div>
        ` : ''}

        <button type="button" class="btn-pip-tile" data-video-id="video-${p.id}" style="position: absolute; bottom: 8px; right: 8px; background: rgba(0,0,0,0.65); border: 1px solid rgba(255,255,255,0.25); color: white; border-radius: 6px; width: 26px; height: 26px; display: flex; align-items: center; justify-content: center; cursor: pointer; z-index: 6; opacity: 0.8; transition: opacity 0.2s;" title="Picture-in-Picture">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
            <rect x="11" y="9" width="9" height="7" rx="1" ry="1" fill="currentColor"></rect>
          </svg>
        </button>

        <div class="tile-overlay-info">
          <span class="tile-mic-icon">${!isScreenTile ? (p.audio_muted ? MIC_MUTED_SVG : MIC_ON_SVG) : ''}</span>

          <span class="tile-name">${p.display_name} ${isLocal && !isScreenTile ? '(You)' : ''}</span>

          <span class="tile-speaking-badge" style="display: ${isSpeaking ? 'inline-flex' : 'none'};">
            <span class="voice-wave ${isSpeaking ? 'active' : ''}">
              <span class="wave-bar"></span>
              <span class="wave-bar"></span>
              <span class="wave-bar"></span>
              <span class="wave-bar"></span>
            </span>
            Speaking
          </span>
          ${p.role === 'host' && !isScreenTile ? `<span class="badge badge-host">Host</span>` : ''}
          ${(p.role === 'moderator' || p.is_cohost) && !isScreenTile ? `<span class="badge badge-moderator" style="background: rgba(168, 85, 247, 0.2); color: #c084fc; border: 1px solid rgba(168, 85, 247, 0.4); font-size: 0.7rem; padding: 2px 6px; border-radius: 4px; font-weight: 600;">Co-Host</span>` : ''}
        </div>
      </div>
    `;
  }

  private bindTileEvents() {
    if (!this.containerEl) return;

    // Pin button click
    this.containerEl.querySelectorAll('.btn-pin-tile:not([data-bound])').forEach(btn => {
      btn.setAttribute('data-bound', '1');
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const tileId = btn.getAttribute('data-tile-id');
        if (tileId) {
          this.pinnedParticipantId = this.pinnedParticipantId === tileId ? null : tileId;
          this.updateGrid();
        }
      });
    });

    // Double click to toggle pin
    this.containerEl.querySelectorAll('.video-tile:not([data-bound])').forEach(tile => {
      tile.setAttribute('data-bound', '1');
      tile.addEventListener('dblclick', () => {
        const id = tile.getAttribute('data-participant-id');
        if (id) {
          this.pinnedParticipantId = this.pinnedParticipantId === id ? null : id;
          this.updateGrid();
        }
      });
    });

    // PiP button
    this.containerEl.querySelectorAll('.btn-pip-tile:not([data-bound])').forEach(btn => {
      btn.setAttribute('data-bound', '1');
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const vidId = btn.getAttribute('data-video-id');
        const video = vidId ? document.getElementById(vidId) as HTMLVideoElement : null;
        if (video && document.pictureInPictureEnabled) {
          if (document.pictureInPictureElement) {
            document.exitPictureInPicture().catch(() => {});
          } else {
            video.requestPictureInPicture().catch(() => {});
          }
        }
      });
    });

    // Click on tile hand raised badge to lower hand
    this.containerEl.querySelectorAll('.tile-hand-raised:not([data-bound])').forEach(badge => {
      badge.setAttribute('data-bound', '1');
      badge.addEventListener('click', (e) => {
        e.stopPropagation();
        const pId = badge.getAttribute('data-participant-id');
        if (pId && this.onLowerHand) {
          this.onLowerHand(pId);
        }
      });
    });
  }
}

const MIC_MUTED_SVG = `
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-danger)" stroke-width="2.5" title="Microphone muted">
    <line x1="1" y1="1" x2="23" y2="23"></line>
    <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
    <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path>
  </svg>
`;

const MIC_ON_SVG = `
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="2.5" title="Microphone unmuted (idle)">
    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
    <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
  </svg>
`;
